import React from 'react';

import { Box, Stack, Typography } from '@mui/material';

export interface DependencyTreeItem {
	id: string;
	label: React.ReactNode;
	icon: React.ReactNode;
	action?: React.ReactNode;
	children?: DependencyTreeItem[];
}

interface DependencyTreeProps {
	root: DependencyTreeItem;
}

export default function DependencyTree({ root }: DependencyTreeProps) {
	return (
		<Box sx={{ py: 0.5 }}>
			<TreeNode item={root} isRoot />
		</Box>
	);
}

interface TreeNodeProps {
	item: DependencyTreeItem;
	isRoot?: boolean;
}

function TreeNode({ item, isRoot = false }: TreeNodeProps) {
	const hasChildren = !!item.children?.length;

	return (
		<Box sx={{ position: 'relative' }}>
			<Stack
				direction="row"
				spacing={1}
				alignItems="center"
				sx={{
					position: 'relative',
					px: 1,
					py: 0.75,
					pr: item.action ? 4 : 1,
					border: '1px solid',
					borderColor: isRoot ? 'primary.200' : 'divider',
					bgcolor: isRoot ? 'primary.50' : 'background.paper',
					borderRadius: 1,
					...(!isRoot && {
						'&::before': {
							content: '""',
							position: 'absolute',
							left: -17,
							top: '50%',
							width: 16,
							borderTop: '2px solid',
							borderColor: 'divider',
						},
					}),
				}}
			>
				<Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>{item.icon}</Box>
				<Typography
					variant="body2"
					sx={{ fontWeight: isRoot ? 600 : 500, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexGrow: 1 }}
				>
					{item.label}
				</Typography>
				{item.action && <Box sx={{ position: 'absolute', right: 4 }}>{item.action}</Box>}
			</Stack>

			{hasChildren && (
				<Box sx={{ ml: isRoot ? 2.25 : 1.75, pl: 2, pt: 1, borderLeft: '2px solid', borderColor: 'divider' }}>
					{item.children!.map((child, index) => (
						<Box key={child.id} sx={{ mt: index === 0 ? 0 : 0.75 }}>
							<TreeNode item={child} />
						</Box>
					))}
				</Box>
			)}
		</Box>
	);
}
